import React, { useEffect, useRef, useState, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import apiClient from '../../api/client';

interface Participant {
  enrollment_id: string;
  nome: string;
  cognome: string;
  email: string;
  azienda?: string;
  enrollment_status: string;
  last_action: 'check_in' | 'check_out' | null;
  last_action_at: string | null;
  history: { action: string; created_at: string }[] | null;
}

interface Edition {
  id: string;
  label: string;
  scheduledAt: string;
  course?: { name: string; inPresenza: boolean };
}

function statusInfo(p: Participant): { label: string; color: string; bg: string; icon: string } {
  if (p.last_action === 'check_in') return { label: 'In aula', color: '#166534', bg: '#dcfce7', icon: '🟢' };
  if (p.last_action === 'check_out') return { label: 'Uscito', color: '#92400e', bg: '#fef3c7', icon: '🟡' };
  return { label: 'Assente', color: '#6b7280', bg: '#f3f4f6', icon: '⚪' };
}

function formatTime(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
}

// ─── QR Scanner Component ─────────────────────────────────────────────────────
function QrScanner({ onScan }: { onScan: (token: string) => void }) {
  const divRef = useRef<HTMLDivElement>(null);
  const scannerRef = useRef<any>(null);
  const [active, setActive] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const start = async () => {
    if (!divRef.current) return;
    try {
      // Dynamic import to avoid SSR issues
      const { Html5Qrcode } = await import('html5-qrcode');
      scannerRef.current = new Html5Qrcode('qr-reader');
      await scannerRef.current.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (decodedText: string) => {
          // Extract token from URL (e.g. http://...?token=abc)
          try {
            const url = new URL(decodedText);
            const token = url.searchParams.get('token');
            if (token) { onScan(token); return; }
          } catch {}
          // Fallback: treat raw text as token
          if (decodedText) onScan(decodedText.trim());
        },
        () => {}
      );
      setActive(true);
      setErr(null);
    } catch (e: any) {
      setErr('Impossibile avviare la fotocamera: ' + (e?.message ?? e));
    }
  };

  const stop = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch {}
      scannerRef.current = null;
    }
    setActive(false);
  };

  useEffect(() => () => { stop(); }, []);

  return (
    <div style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 12, padding: '1.25rem', marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: active ? '1rem' : 0 }}>
        <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: 'var(--gray-800)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          📷 Scanner QR
        </div>
        <button
          onClick={active ? stop : start}
          className={active ? 'btn btn-danger btn-sm' : 'btn btn-primary btn-sm'}
        >
          {active ? '⏹ Ferma' : '▶ Avvia fotocamera'}
        </button>
      </div>
      {err && <div className="alert alert-danger" style={{ marginTop: '0.75rem', marginBottom: 0 }}>{err}</div>}
      <div id="qr-reader" ref={divRef} style={{ display: active ? 'block' : 'none', borderRadius: 8, overflow: 'hidden', marginTop: '0.75rem' }} />
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function AdminCheckInPage() {
  const [editions, setEditions] = useState<Edition[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(false);
  const [scanResult, setScanResult] = useState<{ ok: boolean; msg: string; name?: string } | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'in' | 'out' | 'absent'>('all');

  // Kiosk tokens
  const [kioskTokens, setKioskTokens] = useState<any[]>([]);
  const [kioskForm, setKioskForm] = useState({ pin: '', label: '', hours: '12' });
  const [kioskCreating, setKioskCreating] = useState(false);

  // Load in-presenza editions
  useEffect(() => {
    apiClient.get('/admin/editions').then(res => {
      const all = res.data.editions ?? [];
      setEditions(all.filter((e: any) => e.course?.inPresenza));
    }).catch(() => {});
  }, []);

  const fetchParticipants = useCallback(async (id: string) => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/admin/editions/${id}/attendance`);
      setParticipants(res.data.participants ?? []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (selectedId) fetchParticipants(selectedId); }, [selectedId, fetchParticipants]);

  // Auto-refresh every 15s
  useEffect(() => {
    if (!selectedId) return;
    const id = setInterval(() => fetchParticipants(selectedId), 15_000);
    return () => clearInterval(id);
  }, [selectedId, fetchParticipants]);

  const handleScan = async (token: string) => {
    try {
      const res = await apiClient.post('/admin/attendance/scan', { token });
      const { action, participant } = res.data;
      setScanResult({
        ok: true,
        msg: action === 'check_in' ? 'Entrata registrata' : 'Uscita registrata',
        name: `${participant.nome} ${participant.cognome}`,
      });
      if (selectedId) fetchParticipants(selectedId);
    } catch (e: any) {
      setScanResult({ ok: false, msg: e?.response?.data?.error ?? 'Token non riconosciuto' });
    }
    setTimeout(() => setScanResult(null), 4000);
  };

  const handleManual = async (enrollmentId: string, action: 'check_in' | 'check_out') => {
    await apiClient.post('/admin/attendance/manual', { enrollmentId, action });
    fetchParticipants(selectedId);
  };

  const fetchKioskTokens = useCallback(async (editionId: string) => {
    if (!editionId) return;
    try {
      const res = await apiClient.get(`/admin/kiosk-tokens?editionId=${editionId}`);
      setKioskTokens(res.data.tokens ?? []);
    } catch {}
  }, []);

  useEffect(() => { if (selectedId) fetchKioskTokens(selectedId); }, [selectedId, fetchKioskTokens]);

  const handleCreateKiosk = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!kioskForm.pin || kioskForm.pin.length < 4) return;
    setKioskCreating(true);
    try {
      await apiClient.post('/admin/kiosk-tokens', {
        editionId: selectedId,
        pin: kioskForm.pin,
        label: kioskForm.label || null,
        hours: Number(kioskForm.hours),
      });
      setKioskForm({ pin: '', label: '', hours: '12' });
      fetchKioskTokens(selectedId);
    } catch {} finally { setKioskCreating(false); }
  };

  const handleDeleteKiosk = async (id: string) => {
    await apiClient.delete(`/admin/kiosk-tokens/${id}`);
    fetchKioskTokens(selectedId);
  };

  const filtered = participants.filter(p => {
    const matchSearch = search === '' ||
      `${p.nome} ${p.cognome} ${p.email} ${p.azienda ?? ''}`.toLowerCase().includes(search.toLowerCase());
    const matchFilter =
      filter === 'all' ||
      (filter === 'in' && p.last_action === 'check_in') ||
      (filter === 'out' && p.last_action === 'check_out') ||
      (filter === 'absent' && !p.last_action);
    return matchSearch && matchFilter;
  });

  const stats = {
    total: participants.length,
    in: participants.filter(p => p.last_action === 'check_in').length,
    out: participants.filter(p => p.last_action === 'check_out').length,
    absent: participants.filter(p => !p.last_action).length,
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Gestione Presenze</h1>
        <p className="page-subtitle">Check-in e check-out partecipanti per corsi in presenza</p>
      </div>

      {/* Edition selector */}
      <div className="card" style={{ marginBottom: '1.5rem', padding: '1.25rem' }}>
        <label className="form-label">Seleziona edizione</label>
        <select
          className="form-control"
          value={selectedId}
          onChange={e => { setSelectedId(e.target.value); setParticipants([]); }}
          style={{ maxWidth: 480 }}
        >
          <option value="">— Scegli un'edizione in presenza —</option>
          {editions.map(e => (
            <option key={e.id} value={e.id}>
              {e.course?.name} — {e.label} ({new Date(e.scheduledAt).toLocaleDateString('it-IT')})
            </option>
          ))}
        </select>
        {editions.length === 0 && (
          <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: 'var(--gray-400)' }}>
            Nessuna edizione in presenza trovata. Abilita "Corso in presenza" nelle impostazioni del corso.
          </p>
        )}
      </div>

      {selectedId && (
        <>
          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
            {[
              { label: 'Totale iscritti', value: stats.total, color: 'var(--primary)', icon: '👥' },
              { label: 'In aula', value: stats.in, color: '#16a34a', icon: '🟢' },
              { label: 'Usciti', value: stats.out, color: '#d97706', icon: '🟡' },
              { label: 'Assenti', value: stats.absent, color: 'var(--gray-400)', icon: '⚪' },
            ].map(s => (
              <div key={s.label} className="card" style={{ padding: '1rem 1.25rem', textAlign: 'center' }}>
                <div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>{s.icon}</div>
                <div style={{ fontSize: '1.75rem', fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* QR Scanner */}
          <QrScanner onScan={handleScan} />

          {/* Scan result toast */}
          {scanResult && (
            <div className={`alert ${scanResult.ok ? 'alert-success' : 'alert-danger'}`} style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontSize: '1.25rem' }}>{scanResult.ok ? '✅' : '❌'}</span>
              <div>
                <strong>{scanResult.msg}</strong>
                {scanResult.name && <div style={{ fontSize: '0.875rem' }}>{scanResult.name}</div>}
              </div>
            </div>
          )}

          {/* Search + filter */}
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              className="form-control"
              placeholder="Cerca per nome, email, azienda…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ maxWidth: 320 }}
            />
            {(['all', 'in', 'out', 'absent'] as const).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`btn btn-sm ${filter === f ? 'btn-primary' : 'btn-ghost'}`}
              >
                {{ all: 'Tutti', in: '🟢 In aula', out: '🟡 Usciti', absent: '⚪ Assenti' }[f]}
              </button>
            ))}
            <button onClick={() => fetchParticipants(selectedId)} className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }}>
              🔄 Aggiorna
            </button>
          </div>

          {/* Participant list */}
          {loading ? (
            <div className="loading-container"><div className="spinner" /></div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table className="table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>Partecipante</th>
                    <th>Azienda</th>
                    <th>Stato</th>
                    <th>Ultimo evento</th>
                    <th>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: '2rem' }}>Nessun partecipante trovato</td></tr>
                  )}
                  {filtered.map(p => {
                    const st = statusInfo(p);
                    return (
                      <tr key={p.enrollment_id}>
                        <td>
                          <div style={{ fontWeight: 600 }}>{p.nome} {p.cognome}</div>
                          <div style={{ fontSize: '0.8125rem', color: 'var(--gray-400)' }}>{p.email}</div>
                        </td>
                        <td style={{ color: 'var(--gray-600)', fontSize: '0.9rem' }}>{p.azienda ?? '—'}</td>
                        <td>
                          <span style={{ background: st.bg, color: st.color, padding: '0.25rem 0.75rem', borderRadius: 20, fontSize: '0.8125rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                            {st.icon} {st.label}
                          </span>
                        </td>
                        <td style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
                          {formatTime(p.last_action_at)}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              className="btn btn-success btn-sm"
                              disabled={p.last_action === 'check_in'}
                              onClick={() => handleManual(p.enrollment_id, 'check_in')}
                            >
                              Entra
                            </button>
                            <button
                              className="btn btn-warning btn-sm"
                              disabled={p.last_action === 'check_out' || !p.last_action}
                              onClick={() => handleManual(p.enrollment_id, 'check_out')}
                            >
                              Esce
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {/* ── Kiosk Tokens ── */}
          <div className="card" style={{ marginTop: '2rem', padding: '1.25rem' }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--gray-800)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              📟 Terminali kiosk
            </div>

            {/* Create form */}
            <form onSubmit={handleCreateKiosk} style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.25rem', alignItems: 'flex-end' }}>
              <div>
                <label className="form-label" style={{ fontSize: '0.8rem' }}>PIN (min 4 cifre)</label>
                <input
                  type="password"
                  className="form-control"
                  placeholder="••••"
                  value={kioskForm.pin}
                  onChange={e => setKioskForm(f => ({ ...f, pin: e.target.value }))}
                  style={{ width: 120 }}
                  required
                  minLength={4}
                />
              </div>
              <div>
                <label className="form-label" style={{ fontSize: '0.8rem' }}>Etichetta (opzionale)</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="es. Ingresso A"
                  value={kioskForm.label}
                  onChange={e => setKioskForm(f => ({ ...f, label: e.target.value }))}
                  style={{ width: 180 }}
                />
              </div>
              <div>
                <label className="form-label" style={{ fontSize: '0.8rem' }}>Durata (ore)</label>
                <select
                  className="form-control"
                  value={kioskForm.hours}
                  onChange={e => setKioskForm(f => ({ ...f, hours: e.target.value }))}
                  style={{ width: 100 }}
                >
                  {[4, 8, 12, 24, 48, 72].map(h => <option key={h} value={h}>{h}h</option>)}
                </select>
              </div>
              <button type="submit" className="btn btn-primary btn-sm" disabled={kioskCreating}>
                {kioskCreating ? 'Creazione…' : '+ Crea terminale'}
              </button>
            </form>

            {/* Token list */}
            {kioskTokens.length === 0 ? (
              <p style={{ color: 'var(--gray-400)', fontSize: '0.875rem', margin: 0 }}>Nessun terminale attivo.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {kioskTokens.map((kt: any) => {
                  const kioskUrl = `${window.location.origin}/kiosk?t=${kt.token}`;
                  return (
                    <div key={kt.id} style={{ display: 'flex', gap: '1.25rem', alignItems: 'center', padding: '1rem', background: 'var(--gray-50)', borderRadius: 10, border: '1px solid var(--gray-200)' }}>
                      <QRCodeSVG value={kioskUrl} size={80} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{kt.label || 'Terminale kiosk'}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginTop: '0.15rem', wordBreak: 'break-all' }}>{kioskUrl}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>
                          Scade: {new Date(kt.expires_at).toLocaleString('it-IT')}
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', flexShrink: 0 }}>
                        <a href={kioskUrl} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm">
                          🔗 Apri
                        </a>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeleteKiosk(kt.id)}>
                          Revoca
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
